import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowPathIcon,
  SwatchIcon,
  AdjustmentsHorizontalIcon,
  PuzzlePieceIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../hooks/useTheme';
import { ACCENTS, SKINS, type Density } from '../contexts/theme';
import { usePermissoes } from '../hooks/usePermissoes';
import { parametrosApi, type Parametros } from '../api/parametros';
import { CATEGORIAS_NOTIFICACAO } from '../api/notificacoes';
import { useNotificacaoPrefs } from '../hooks/useNotificacaoPrefs';
import { useEstatisticasUsuarios } from '../hooks/useUsuarios';
import { useCloudflareConfig } from '../hooks/useCloudflare';
import { useToast } from '../components/ui/Toast';
import {
  PainelIris,
  LinhaConfig,
  ToggleIris,
  BadgeEmBreve,
  inputIris,
  btnPrimario,
  btnSecundario,
} from '../components/ui/iris';

/**
 * Hub de configurações (handoff design_handoff_sistema). Acessível a qualquer
 * logado: Aparência é por usuário (Íris prefs); Geral/Integrações editam os
 * parâmetros globais (mesma API da Organização → Parâmetros) e aparecem só pra
 * ADMIN. Faturamento/Notificações/Segurança são stubs.
 */

type SecaoId = 'aparencia' | 'geral' | 'integracoes' | 'faturamento' | 'notificacoes' | 'seguranca';

interface SecaoDef {
  id: SecaoId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

const SECOES: SecaoDef[] = [
  { id: 'aparencia', label: 'Aparência', icon: SwatchIcon },
  { id: 'geral', label: 'Geral', icon: AdjustmentsHorizontalIcon, adminOnly: true },
  { id: 'integracoes', label: 'Integrações', icon: PuzzlePieceIcon, adminOnly: true },
  { id: 'faturamento', label: 'Faturamento', icon: CreditCardIcon },
  { id: 'notificacoes', label: 'Notificações', icon: BellIcon },
  { id: 'seguranca', label: 'Segurança', icon: ShieldCheckIcon },
];

const ConfiguracoesPage: React.FC = () => {
  const { isAdmin } = usePermissoes();
  const toast = useToast();
  const location = useLocation();
  // Preferências de notificação são POR USUÁRIO (qualquer um edita as suas) —
  // diferente dos parâmetros globais abaixo, que são só ADMIN.
  const { silenciado, alternar } = useNotificacaoPrefs();

  const secoes = SECOES.filter(s => !s.adminOnly || isAdmin);
  const [secaoAtiva, setSecaoAtiva] = useState<SecaoId>('aparencia');
  const refs = useRef<Partial<Record<SecaoId, HTMLElement | null>>>({});

  // Parâmetros globais (só ADMIN carrega/edita — endpoint é AdminPolicy).
  const [param, setParam] = useState<Parametros | null>(null);
  const originalRef = useRef<Parametros | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const p = await parametrosApi.obter();
        setParam(p);
        originalRef.current = p;
      } catch { /* sem parâmetros ainda */ }
    })();
  }, [isAdmin]);

  // Âncora inicial (#aparencia etc — usado pelo ⌘K).
  useEffect(() => {
    const hash = location.hash.replace('#', '') as SecaoId;
    if (hash && SECOES.some(s => s.id === hash)) {
      setSecaoAtiva(hash);
      setTimeout(() => refs.current[hash]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [location.hash]);

  const irParaSecao = (id: SecaoId) => {
    setSecaoAtiva(id);
    refs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const registrarRef = useCallback((id: SecaoId) => (el: HTMLElement | null) => {
    refs.current[id] = el;
  }, []);

  const alterado = param && originalRef.current && JSON.stringify(param) !== JSON.stringify(originalRef.current);

  const salvar = async () => {
    if (!param) return;
    setSalvando(true);
    try {
      await parametrosApi.salvar(param);
      originalRef.current = param;
      toast.sucesso('Configurações salvas com sucesso!');
    } catch { toast.erro('Erro ao salvar configurações'); }
    finally { setSalvando(false); }
  };

  const cancelar = () => {
    if (originalRef.current) setParam(originalRef.current);
  };

  return (
    // Mock Íris: conteúdo limitado a 1080px centralizado.
    <div className="max-w-[980px] mx-auto space-y-4">
      {/* Header (mock: sem ícone) */}
      <div>
        <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Configurações</h1>
        <p className="text-[13.5px] text-tx-3 mt-0.5">Preferências pessoais e configurações do sistema.</p>
      </div>

      <div className="flex gap-5 items-start">
        {/* Nav secundária */}
        <nav className="hidden md:block w-[200px] shrink-0 sticky top-4 space-y-0.5">
          {secoes.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => irParaSecao(s.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-iris-sm transition-colors text-left ${
                secaoAtiva === s.id
                  ? 'bg-accent-soft text-accent-text font-semibold'
                  : 'text-tx-2 hover:bg-surface-2 hover:text-tx'
              }`}
            >
              <s.icon className="w-4 h-4 shrink-0" />
              {s.label}
            </button>
          ))}
        </nav>

        {/* Conteúdo */}
        <div className="flex-1 min-w-0 space-y-4 pb-16">
          {/* ── Aparência (real — Íris prefs por usuário) ── */}
          <section id="aparencia" ref={registrarRef('aparencia')} className="scroll-mt-4">
            <SecaoAparencia />
          </section>

          {isAdmin && (
            <>
              {/* ── Geral (real — parâmetros globais) ── */}
              <section id="geral" ref={registrarRef('geral')} className="scroll-mt-4">
                <PainelIris titulo="Geral">
                  <p className="text-[13px] text-tx-3 mb-1">
                    Configurações gerais da organização. Dados cadastrais (razão social, CNPJ, endereço)
                    ficam em <Link to="/organizacao" className="text-accent-text underline">Organização → Cadastro</Link>.
                  </p>
                  <LinhaConfig
                    titulo="Monitoramento automático de CTe"
                    descricao="Consulta a SEFAZ periodicamente para baixar CTes de terceiros."
                  >
                    {param ? (
                      <ToggleIris
                        ativo={param.cteMonitoramentoAtivo}
                        onChange={v => setParam({ ...param, cteMonitoramentoAtivo: v })}
                        aria-label="Monitoramento automático de CTe"
                      />
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Conferência — permitir NF-e do manifesto (XML)"
                    descricao="Aceita XML vindo do manifesto (Distribuição DFe) como fonte de conferência, além do upload manual."
                  >
                    {param ? (
                      <ToggleIris
                        ativo={param.conferenciaPermiteManifesto}
                        onChange={v => setParam({ ...param, conferenciaPermiteManifesto: v })}
                        aria-label="Conferência — permitir NF-e do manifesto (XML)"
                      />
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Conferência — permitir contagem manual"
                    descricao="Permite conferir itens contando manualmente, sem coletor ou leitor de código de barras."
                  >
                    {param ? (
                      <ToggleIris
                        ativo={param.conferenciaContagemManual}
                        onChange={v => setParam({ ...param, conferenciaContagemManual: v })}
                        aria-label="Conferência — permitir contagem manual"
                      />
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Conferência — multiplicador na bipagem"
                    descricao='Permite o atalho "12*código" na leitura para somar N vezes de uma vez (multiplica a embalagem lida).'
                  >
                    {param ? (
                      <ToggleIris
                        ativo={param.conferenciaMultiplicadorAtivo}
                        onChange={v => setParam({ ...param, conferenciaMultiplicadorAtivo: v })}
                        aria-label="Conferência — multiplicador na bipagem"
                      />
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Retenção da auditoria"
                    descricao="Eventos de auditoria mais antigos que esta janela são removidos de madrugada. 'Nunca' mantém o histórico completo."
                  >
                    {param ? (
                      <select
                        value={param.auditoriaRetencaoDias}
                        onChange={e => setParam({ ...param, auditoriaRetencaoDias: Number(e.target.value) })}
                        className={`w-44 ${inputIris}`}
                      >
                        <option value={0}>Nunca (manter tudo)</option>
                        <option value={30}>30 dias</option>
                        <option value={60}>60 dias</option>
                        <option value={90}>90 dias</option>
                        <option value={180}>180 dias</option>
                        <option value={365}>1 ano</option>
                        <option value={730}>2 anos</option>
                      </select>
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Retenção dos logs técnicos"
                    descricao="Logs (Warning+) mais antigos que esta janela são removidos de madrugada, junto com a auditoria. Log técnico costuma viver menos."
                  >
                    {param ? (
                      <select
                        value={param.logRetencaoDias}
                        onChange={e => setParam({ ...param, logRetencaoDias: Number(e.target.value) })}
                        className={`w-44 ${inputIris}`}
                      >
                        <option value={0}>Nunca (manter tudo)</option>
                        <option value={7}>7 dias</option>
                        <option value={30}>30 dias</option>
                        <option value={90}>90 dias</option>
                        <option value={180}>180 dias</option>
                      </select>
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Retenção das notificações"
                    descricao="Notificações do sino mais antigas que esta janela são removidas de madrugada. 'Limpar' no sino some só pra você; isto apaga do banco pra todos."
                  >
                    {param ? (
                      <select
                        value={param.notificacaoRetencaoDias}
                        onChange={e => setParam({ ...param, notificacaoRetencaoDias: Number(e.target.value) })}
                        className={`w-44 ${inputIris}`}
                      >
                        <option value={0}>Nunca (manter tudo)</option>
                        <option value={30}>30 dias</option>
                        <option value={90}>90 dias</option>
                        <option value={180}>180 dias</option>
                        <option value={365}>1 ano</option>
                      </select>
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Retenção da telemetria de uso"
                    descricao="Por quantos dias guardar as contagens de uso por tela e recurso. 0 = manter para sempre."
                    ultima
                  >
                    {param ? (
                      <select
                        value={param.telemetriaRetencaoDias}
                        onChange={e => setParam({ ...param, telemetriaRetencaoDias: Number(e.target.value) })}
                        className={`w-44 ${inputIris}`}
                      >
                        <option value={0}>Nunca (manter tudo)</option>
                        <option value={30}>30 dias</option>
                        <option value={90}>90 dias</option>
                        <option value={180}>180 dias</option>
                        <option value={365}>1 ano</option>
                      </select>
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                </PainelIris>
              </section>

              {/* ── Integrações (real — parâmetros + status Cloudflare) ── */}
              <section id="integracoes" ref={registrarRef('integracoes')} className="scroll-mt-4">
                <PainelIris titulo="Integrações">
                  <LinhaConfig
                    titulo="URL da tela inicial / BI"
                    descricao="Dashboard embutido em /dashboard (Metabase, Power BI, Grafana…). Em branco oculta a aba."
                  >
                    {param ? (
                      <input
                        type="url"
                        value={param.metabaseUrl ?? ''}
                        onChange={e => setParam({ ...param, metabaseUrl: e.target.value })}
                        placeholder="https://exemplo.com/dashboard/..."
                        className={`w-64 ${inputIris}`}
                      />
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Recálculo automático da Reposição"
                    descricao="Pré-calcula o panorama de marcas de madrugada."
                  >
                    {param ? (
                      <>
                        <input
                          type="time"
                          value={param.reposicaoHorario || '02:30'}
                          onChange={e => setParam({ ...param, reposicaoHorario: e.target.value })}
                          disabled={!param.reposicaoCalculosAtivos}
                          className={`w-28 ${inputIris}`}
                        />
                        <ToggleIris
                          ativo={param.reposicaoCalculosAtivos}
                          onChange={v => setParam({ ...param, reposicaoCalculosAtivos: v })}
                          aria-label="Recálculo automático da Reposição"
                        />
                      </>
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Resumo diário da Reposição"
                    descricao="Notifica itens acabando, abaixo do mínimo e dead stock após cada recálculo. Só avisa quando há algo acionável."
                  >
                    {param ? (
                      <ToggleIris
                        ativo={param.reposicaoDigestAtivo}
                        onChange={v => setParam({ ...param, reposicaoDigestAtivo: v })}
                        aria-label="Resumo diário da Reposição"
                      />
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Janela de análise da Reposição (padrão)"
                    descricao="Meses de vendas usados pra giro e sugestão quando o usuário não escolhe outro valor na toolbar."
                  >
                    {param ? (
                      <select
                        value={param.janelaAnaliseMesesDefault}
                        onChange={e => setParam({ ...param, janelaAnaliseMesesDefault: Number(e.target.value) })}
                        className={`w-32 ${inputIris}`}
                      >
                        <option value={12}>12 meses</option>
                        <option value={9}>9 meses</option>
                        <option value={6}>6 meses</option>
                        <option value={3}>3 meses</option>
                        <option value={2}>2 meses</option>
                        <option value={1}>1 mês</option>
                      </select>
                    ) : <span className="text-xs text-tx-3">—</span>}
                  </LinhaConfig>
                  <LinhaConfig
                    titulo="Cloudflare Tunnel"
                    descricao="Acesso externo ao Bn UtilyHub via Cloudflare."
                    ultima
                  >
                    <StatusCloudflare />
                  </LinhaConfig>
                </PainelIris>
              </section>
            </>
          )}

          {/* ── Faturamento (stub) ── */}
          {/* TODO(backend): plano e limite de assentos não existem */}
          <section id="faturamento" ref={registrarRef('faturamento')} className="scroll-mt-4">
            <PainelIris titulo="Faturamento" acao={<BadgeEmBreve />}>
              <LinhaConfig titulo="Plano" descricao="Plano contratado do Bn UtilyHub.">
                <span className="text-sm font-semibold text-tx-3">—</span>
              </LinhaConfig>
              <LinhaConfig titulo="Assentos do plano" descricao="Usuários ativos em relação ao limite do plano." ultima>
                {isAdmin ? <AssentosAtivos /> : <span className="text-sm font-semibold text-tx-3">—</span>}
              </LinhaConfig>
            </PainelIris>
          </section>

          {/* ── Notificações (stub) ── */}
          {/* TODO(backend): serviço de notificação email/push não existe */}
          <section id="notificacoes" ref={registrarRef('notificacoes')} className="scroll-mt-4">
            <PainelIris titulo="Notificações">
              <p className="pb-1 text-xs text-tx-3">
                O que chega no seu sino, em tempo real. Vale só pra você — não muda o que os
                outros usuários recebem. Desligar some com a categoria do feed e do contador.
              </p>
              {CATEGORIAS_NOTIFICACAO.map((c, i) => (
                <LinhaConfig
                  key={c.prefixo}
                  titulo={c.label}
                  descricao={c.descricao}
                  ultima={i === CATEGORIAS_NOTIFICACAO.length - 1}
                >
                  <ToggleIris
                    ativo={!silenciado(c.prefixo)}
                    onChange={(v) => alternar(c.prefixo, v)}
                    aria-label={c.label}
                  />
                </LinhaConfig>
              ))}
            </PainelIris>
          </section>

          {/* ── Segurança (stub) ── */}
          {/* TODO(backend): 2FA e tracking de sessões não existem */}
          <section id="seguranca" ref={registrarRef('seguranca')} className="scroll-mt-4">
            <PainelIris titulo="Segurança" acao={<BadgeEmBreve />}>
              <LinhaConfig titulo="Autenticação em dois fatores (2FA)" descricao="Código adicional no login para proteger sua conta.">
                <ToggleIris ativo={false} disabled aria-label="Autenticação em dois fatores" />
              </LinhaConfig>
              <LinhaConfig titulo="Sessões ativas" descricao="Dispositivos e navegadores conectados à sua conta." ultima>
                <span className="text-xs text-tx-3">Indisponível</span>
              </LinhaConfig>
            </PainelIris>
          </section>

          {/* Barra de alterações não salvas (só ADMIN — parâmetros globais).
              Mesmo idioma da matriz de papéis: sticky DENTRO da coluna de
              conteúdo, então tem exatamente a largura dos cards. Antes ficava
              fora do container, com margens negativas (-mx-6) pra "alcançar" o
              padding do <main> — que não alcança, porque o container é
              centralizado: a barra vazava 24px além dos cards e os botões
              ficavam desalinhados da borda direita. E só aparece quando há o
              que salvar — barra permanente com botões desabilitados é ruído. */}
          {isAdmin && param && alterado && (
            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-iris border border-accent-ring bg-accent-soft px-3 py-2.5">
              <p className="text-[13px] text-tx">Parâmetros do sistema com <strong>alterações não salvas</strong>.</p>
              <div className="flex gap-2">
                <button type="button" onClick={cancelar} disabled={salvando} className={`${btnSecundario} h-8 px-3 text-xs`}>
                  Descartar
                </button>
                <button type="button" onClick={salvar} disabled={salvando} className={`${btnPrimario} h-8 px-3 text-xs`}>
                  {salvando && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
                  {salvando ? 'Salvando…' : 'Salvar alterações'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Aparência ──────────────────────────────────────────────────────────────

const SecaoAparencia: React.FC = () => {
  const { theme, toggleTheme, density, setDensity, accent, setAccent, skin, setSkin } = useTheme();

  return (
    <PainelIris titulo="Aparência">
      <LinhaConfig titulo="Tema" descricao="Claro ou escuro — aplica na hora e fica salvo neste navegador.">
        <Segmented
          opcoes={[{ valor: 'light', label: 'Claro' }, { valor: 'dark', label: 'Escuro' }]}
          valor={theme}
          onChange={(t) => { if (theme !== t) toggleTheme(); }}
        />
      </LinhaConfig>
      <LinhaConfig titulo="Densidade" descricao="Compacto reduz alturas e fontes do shell — mais conteúdo na tela.">
        <Segmented
          opcoes={[{ valor: 'comfortable', label: 'Confortável' }, { valor: 'compact', label: 'Compacto' }]}
          valor={density}
          onChange={(d) => setDensity(d as Density)}
        />
      </LinhaConfig>
      <LinhaConfig titulo="Cor de destaque" descricao="Reflete em botões, links e navegação.">
        <div className="flex items-center gap-2.5">
          {ACCENTS.map((a) => (
            <button
              key={a.hex}
              type="button"
              onClick={() => setAccent(a.hex)}
              title={a.nome}
              aria-label={`Cor de destaque ${a.nome}`}
              aria-pressed={accent === a.hex}
              className="h-[26px] w-[26px] rounded-full transition-transform hover:scale-110"
              style={{
                backgroundColor: a.hex,
                boxShadow: accent === a.hex ? '0 0 0 2px var(--surface), 0 0 0 4px var(--text)' : undefined,
              }}
            />
          ))}
        </div>
      </LinhaConfig>
      <LinhaConfig titulo="Cor do menu" descricao="Pinta a barra lateral e o topo. 'Claro' segue o tema." ultima>
        <div className="flex flex-wrap items-center gap-2">
          {SKINS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSkin(s.id)}
              aria-pressed={skin === s.id}
              className={`flex items-center gap-2 rounded-iris-sm border px-2.5 py-1.5 text-sm transition-colors ${
                skin === s.id
                  ? 'border-accent bg-accent-soft text-accent-text font-semibold'
                  : 'border-line text-tx-2 hover:bg-surface-2'
              }`}
            >
              <span className="h-4 w-4 rounded-full border border-line" style={{ backgroundColor: s.cor }} />
              {s.nome}
            </button>
          ))}
        </div>
      </LinhaConfig>
    </PainelIris>
  );
};

const Segmented: React.FC<{
  opcoes: { valor: string; label: string }[];
  valor: string;
  onChange: (v: string) => void;
}> = ({ opcoes, valor, onChange }) => (
  <div className="inline-flex gap-1 bg-surface-2 p-1 rounded-iris">
    {opcoes.map(o => (
      <button
        key={o.valor}
        type="button"
        aria-pressed={valor === o.valor}
        onClick={() => onChange(o.valor)}
        className={`px-3 py-1.5 text-xs font-medium rounded-iris-sm transition-colors ${
          valor === o.valor ? 'bg-accent text-white' : 'text-tx-2 hover:text-tx'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// ── Auxiliares (só montados pra ADMIN — evita 403 em USER comum) ───────────

const AssentosAtivos: React.FC = () => {
  const { data: stats } = useEstatisticasUsuarios();
  const ativos = stats?.usuariosAtivos;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-semibold text-tx">{ativos ?? '—'} <span className="font-normal text-tx-3">de — em uso</span></span>
      {/* Barra fica vazia até existir limite de assentos no backend. */}
      <div className="w-40 h-2 rounded-full bg-surface-2 overflow-hidden">
        <div className="h-full bg-accent rounded-full" style={{ width: '0%' }} />
      </div>
    </div>
  );
};

const StatusCloudflare: React.FC = () => {
  const { data: cfg } = useCloudflareConfig();
  return (
    <div className="flex items-center gap-2">
      {cfg?.hostname ? (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold rounded-iris-sm bg-ok-soft text-ok">
          Conectado · <span className="font-mono">{cfg.hostname}</span>
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-iris-sm bg-surface-2 text-tx-2">
          Não configurado
        </span>
      )}
      <Link to="/organizacao" className="text-xs text-accent-text underline">Gerenciar</Link>
    </div>
  );
};

export default ConfiguracoesPage;
